<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccountResource } from 'dashboard/composables/useAccountResource';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import GgSupportAPI from 'dashboard/api/ggSupport';
import GgCustomersAPI from 'dashboard/api/ggCustomers';
import { useClipboard } from '@vueuse/core';
import GgSupportSearch from 'dashboard/components-next/gg-support/GgSupportSearch.vue';
import GgSupportSelect from 'dashboard/components-next/gg-support/GgSupportSelect.vue';
import {
  formatGgCustomerName,
  formatGgCustomerLocation,
} from 'dashboard/components-next/gg-support/ggCustomerPresentation';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contact: { type: Object, required: true },
  conversationId: { type: Number, default: null },
  contactId: { type: Number, default: null },
  detailed: { type: Boolean, default: false },
});

const { t, locale } = useI18n();
const { copy, copied } = useClipboard();
const customerSearchEnabled = useAccountResource('gg_customer_search');
const store = useStore();
const { run } = useAbortableRequest();
const selected = ref(null);
const editing = ref(false);
const saving = ref(false);
const problem = ref('');
const saved = ref(false);
const searchMode = ref('name');
watch(customerSearchEnabled, enabled => {
  if (!enabled) {
    selected.value = null;
    editing.value = false;
    problem.value = '';
    saved.value = false;
  }
});
const searchOptions = computed(() => [
  { value: 'name', label: t('GG_CONTACT.SEARCH_NAME') },
  { value: 'document', label: t('GG_CONTACT.DOCUMENT') },
  { value: 'id', label: t('GG_CONTACT.CUSTOMER_ID') },
  {
    value: 'email',
    label: t('GG_CONTACT.SEARCH_EMAIL'),
  },
  {
    value: 'phone',
    label: t('GG_CONTACT.SEARCH_PHONE'),
  },
]);
const searchLabel = computed(
  () =>
    searchOptions.value.find(option => option.value === searchMode.value).label
);
const searchHint = computed(() => {
  const hints = {
    name: 'GG_CONTACT.SEARCH_NAME_HINT',
    document: 'GG_CONTACT.SEARCH_DOCUMENT_HINT',
    id: 'GG_CONTACT.SEARCH_ID_HINT',
    email: 'GG_CONTACT.SEARCH_EMAIL_HINT',
    phone: 'GG_CONTACT.SEARCH_PHONE_HINT',
  };
  return t(hints[searchMode.value] || 'GG_CONTACT.SEARCH_HINT');
});
watch(searchMode, () => {
  selected.value = null;
  problem.value = '';
  saved.value = false;
});
const customer = computed(() => props.contact.custom_attributes?.gg_customer);
const showSearch = computed(() => !customer.value || editing.value);
const fields = computed(() => [
  { label: t('GG_CONTACT.CUSTOMER_ID'), value: customer.value.id, copy: true },
  {
    label: t('GG_CONTACT.LEGAL_NAME'),
    value: formatGgCustomerName(customer.value.name),
  },
  {
    label: t('GG_CONTACT.TRADE_NAME'),
    value: formatGgCustomerName(customer.value.trade_name),
  },
  { label: t('GG_CONTACT.DOCUMENT'), value: customer.value.document },
  ...(props.detailed
    ? [
        {
          label: t('GG_CONTACT.COMMERCIAL_REGION'),
          value: customer.value.commercial_region,
        },
        { label: t('GG_CONTACT.CITY'), value: customer.value.city },
        { label: t('GG_CONTACT.STATE'), value: customer.value.state },
        {
          label: t('GG_CONTACT.MACRO_REGION'),
          value: customer.value.macro_region,
        },
      ]
    : []),
  ...(!props.detailed
    ? [
        {
          label: t('GG_CONTACT.LOCATION'),
          value: formatGgCustomerLocation(customer.value),
        },
      ]
    : []),
]);
const updatedAt = computed(() =>
  new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(customer.value.synced_at))
);

const saveCustomer = async candidate => {
  saving.value = true;
  problem.value = '';
  saved.value = false;
  try {
    const response = await run(signal =>
      props.contactId
        ? GgCustomersAPI.link(props.contactId, candidate.id, { signal })
        : GgSupportAPI.linkCustomer(props.conversationId, candidate.id, {
            signal,
          })
    );
    if (!response) return;
    await store.dispatch('contacts/updateContact', response.data.payload);
    selected.value = null;
    editing.value = false;
    saved.value = true;
  } catch (error) {
    const code = error.response?.data?.code;
    if (code === 'CUSTOMER_NOT_FOUND') {
      problem.value = t('GG_CONTACT.NOT_FOUND');
    } else {
      problem.value = t(
        code === 'CUSTOMER_ACCESS_DENIED' || code === 'SUPPORT_ACCESS_DENIED'
          ? 'GG_SUPPORT.CUSTOMER_SEARCH.ACCESS_DENIED'
          : 'GG_CONTACT.SAVE_FAILED'
      );
    }
  } finally {
    saving.value = false;
  }
};

const edit = () => {
  selected.value = null;
  problem.value = '';
  saved.value = false;
  editing.value = true;
};
</script>

<template>
  <div class="flex min-w-0 flex-col gap-3">
    <template v-if="customer">
      <dl class="m-0 flex flex-col gap-2">
        <div v-for="field in fields" :key="field.label" class="min-w-0">
          <dt class="text-xs font-normal text-n-slate-11">{{ field.label }}</dt>
          <dd class="m-0 break-words text-sm text-n-slate-12">
            <span>{{ field.value || t('GG_CONTACT.NOT_AVAILABLE') }}</span>
            <Button
              v-if="field.copy"
              :label="t(copied ? 'GG_CONTACT.COPIED_ID' : 'GG_CONTACT.COPY_ID')"
              icon="i-lucide-copy"
              size="sm"
              variant="ghost"
              color="slate"
              @click="copy(String(field.value))"
            />
          </dd>
        </div>
      </dl>
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('GG_CONTACT.UPDATED_AT', { date: updatedAt }) }}
      </p>
      <div
        v-if="customerSearchEnabled && !editing"
        class="flex flex-wrap gap-2"
      >
        <Button
          :label="t('GG_CONTACT.REFRESH')"
          icon="i-lucide-refresh-cw"
          size="sm"
          variant="faded"
          color="slate"
          :is-loading="saving"
          :disabled="saving"
          @click="saveCustomer(customer)"
        />
        <Button
          :label="t('GG_CONTACT.CHANGE')"
          size="sm"
          variant="ghost"
          color="slate"
          :disabled="saving"
          @click="edit"
        />
      </div>
    </template>
    <p v-else class="m-0 text-sm text-n-slate-11">
      {{ t('GG_CONTACT.EMPTY') }}
    </p>
    <p
      v-if="!customerSearchEnabled"
      role="status"
      class="m-0 text-sm text-n-slate-11"
    >
      {{ t('ACCOUNT_RESOURCES.DISABLED') }}
    </p>
    <template v-if="customerSearchEnabled && showSearch">
      <GgSupportSelect
        id="gg-contact-search-mode"
        v-model="searchMode"
        :label="t('GG_CONTACT.SEARCH_BY')"
        :options="searchOptions"
        :disabled="saving"
      />
      <GgSupportSearch
        :key="searchMode"
        v-model="selected"
        input-id="gg-contact-customer"
        :conversation-id="conversationId"
        :contact-id="contactId"
        :label="searchLabel"
        :placeholder="searchLabel"
        :hint="searchHint"
        :customer-search-mode="searchMode"
        :disabled="saving"
        hide-label
        available
      />
      <p v-if="selected" class="m-0 text-xs text-n-slate-11">
        {{ t('GG_CONTACT.CONFIRM_HINT') }}
      </p>
      <div v-if="selected || editing" class="flex flex-wrap gap-2">
        <Button
          v-if="selected"
          :label="t(customer ? 'GG_CONTACT.REPLACE' : 'GG_CONTACT.LINK')"
          icon="i-lucide-link"
          size="sm"
          :is-loading="saving"
          :disabled="saving"
          @click="saveCustomer(selected)"
        />
        <Button
          v-if="editing"
          :label="t('GG_CONTACT.CANCEL')"
          size="sm"
          variant="ghost"
          color="slate"
          :disabled="saving"
          @click="editing = false"
        />
      </div>
    </template>
    <p v-if="problem" role="alert" class="m-0 text-xs text-n-ruby-11">
      {{ problem }}
    </p>
    <p v-else-if="saved" role="status" class="m-0 text-xs text-n-teal-11">
      {{ t('GG_CONTACT.SAVED') }}
    </p>
  </div>
</template>
